import React, { useContext } from "react";
import { BoundedContent, Box, Column, GridContainer, Row, useScreenClass } from "@vp/swan";
import { PageStaticDataContext } from "../../contexts/PageStaticDataContextProvider";
import { ProductContext } from "../../contexts/ProductContextProvider";
import { ReferentialImageContext } from "../../contexts/ReferentialImagesContext";
import { useSnackbarContext } from "../../contexts/SnackbarContext";
import useGetStudioDocumentUrl from "../../hooks/useGetStudioDocumentUrl";
import { PluginDataContext } from "../../pluginsInfrastructure/PluginDataContextProvider";
import PluginPlaceholder from "../../pluginsInfrastructure/PluginPlaceholder";
import { Placeholder } from "../../pluginsInfrastructure/PluginRegistryTypes";
import { getScreenClass } from "../../utils/screenClass";
import { HeroGallery } from "@vp/core-hero-gallery";
import { analyticsPageName } from "../../utils/analyticsConfig";
import { Logger } from "../../logging/logger";
import { BffDataContext } from "../../contexts/BffDataContextProvider";
import StickyBox from "../../components/StickyBox";
import PageConfig from "../../utils/PageConfig";
import { useExperimentsContext } from "../../contexts/ExperimentsProvider";
import { RevisedDesignFirstFlowContext } from "../../utils/abTests/abTests";
import { GalleryTemplateContext } from "../../contexts/GalleryTemplateProvider";
import { FavoriteHeartProvider, ProductFavoriteHeart } from "@vp/favorites-heart";
import { FeedTemplateContext } from "../../contexts/FeedTemplateProvider";

export interface HeroGalleryProps {}

const HeroGalleryWrapper = () => {
  const { useExperiment } = useExperimentsContext();
  const revisedDesignFirstFlowVariation = useExperiment(RevisedDesignFirstFlowContext);
  const { heroGallery, mpv, tenant, locale } = useContext(PageStaticDataContext);
  const { pluginData } = useContext(PluginDataContext);

  const { selectedReferentialImage } = useContext(ReferentialImageContext);
  const {
    selections,
    showReferentialImage,
    selectCompatibleValue,
    designOptions = {},
  } = useContext(ProductContext);
  const { showSnackbarMessage } = useSnackbarContext();
  const { studioDocumentUrl } = useGetStudioDocumentUrl();
  const { canonicalDomain } = useContext(PageStaticDataContext);
  const { isMobile } = getScreenClass(useScreenClass());
  const { prices } = useContext(BffDataContext);
  const configuration =
    prices[parseInt(Object.keys(prices)[0])]?.possibleConfigurations &&
    prices[parseInt(Object.keys(prices)[0])]?.possibleConfigurations[0];

  const { designId = "", fullProductOptions } = useContext(GalleryTemplateContext);
  const feedData = useContext(FeedTemplateContext);
  function getDictionaryEntryValue(key: string): string {
    return pluginData.contentful?.[key] || "";
  }

  const props = {
    heroGallery,
    mpv,
    tenant,
    locale,
    localizations: {
      heroGallery: {
        heroThumbnailAltText: getDictionaryEntryValue("heroThumbnailAltText"),
        heroDefaultAltText: getDictionaryEntryValue("heroDefaultAltText"),
        heroImageAltTextPrefix: getDictionaryEntryValue("heroImageAltTextPrefix"),
        heroGalleryAriaLabel: getDictionaryEntryValue("heroGalleryAriaLabel"),
        heroThumbnailAriaLabel: getDictionaryEntryValue("heroThumbnailAriaLabel"),
        heroImageAriaLabel: getDictionaryEntryValue("heroImageAriaLabel"),
      },
      vortex: {
        vortexLoadError: getDictionaryEntryValue("vortexLoadError"),
        vortexToggleView: getDictionaryEntryValue("vortexToggleView"),
        vortexLoadingPreview: getDictionaryEntryValue("vortexLoadingPreview"),
        vortexActionsFront: getDictionaryEntryValue("vortexActionsFront"),
        vortexActionsBack: getDictionaryEntryValue("vortexActionsBack"),
        vortexActionsOpen: getDictionaryEntryValue("vortexActionsOpen"),
        vortexActionsFolded: getDictionaryEntryValue("vortexActionsFolded"),
        vortexActionsUnfolded: getDictionaryEntryValue("vortexActionsUnfolded"),
        vortexActionsClose: getDictionaryEntryValue("vortexActionsClose"),
      },
    },
    selectedReferentialImage,
    selections,
    showSnackbarMessage,
    studioDocumentUrl,
    canonicalDomain,
    showReferentialImage,
    trackingOptions: {
      pageStage: "Discover",
      pageSection: "Product Page",
      analyticsPageName: analyticsPageName(mpv.mpvId),
    },
    logger: {
      warn: Logger.instance.warning,
      info: Logger.instance.info,
      error: Logger.instance.error,
    },
    selectCompatibleValue,
    possibleConfiguration:
      revisedDesignFirstFlowVariation === "test" && designId ? fullProductOptions : configuration,
    requestor: PageConfig.requestor,
    templateInfo: feedData?.templateInformation
      ? {
          previewUrl: feedData.templateInformation.previewUrl,
          altText: feedData.templateInformation.altText,
        }
      : undefined,
    templateDetails: {
      designId: designId,
      designOptions: designOptions,
    },
  };

  return (
    <>
      <BoundedContent>
        <GridContainer>
          <Row className="carousel-row-container">
            <Column span={7} pb={isMobile ? "5" : 0}>
              <StickyBox>
                <Box padding={4} style={{ position: "absolute", top: 0, right: 0, zIndex: 3 }}>
                  <FavoriteHeartProvider
                    locale={locale}
                    pageSection={props.trackingOptions.pageSection}
                    disableLogging={PageConfig.isDevelopment}
                  >
                    <ProductFavoriteHeart
                      productKey={props.mpv.coreProductId}
                      productVersion={props.mpv.version}
                      selectedOptions={{}}
                      productName={props.mpv.name}
                      mpvId={props.mpv.mpvId}
                      pageZone="Product Image"
                    />
                  </FavoriteHeartProvider>
                </Box>
                <HeroGallery {...props} />
              </StickyBox>
            </Column>
            <Column span={5}>
              <PluginPlaceholder placeholderType={Placeholder.ProductBuilder} />
            </Column>
          </Row>
        </GridContainer>
      </BoundedContent>
    </>
  );
};

export default HeroGalleryWrapper;
